import { useMemo, useState, useEffect, useRef } from 'react';
import { Checkbox } from 'antd';
import { ChevronDownIcon } from '@/assets/icons/icons';

type Level = { id: string | number; display_name?: string; name?: string };
type Courier = {
  id: string | number;
  name?: string;
  title?: string;
  courier_name?: string;
  service_levels?: Level[];
};

type Opt = { id: string | number; name: string; service_levels?: Level[] };

export type CourierSubFilterMenuBodyProps = {
  source: Courier[];
  searchQuery: string;
  selectedOptions: Opt[];
  setSelectedOptions?: (opts: Opt[]) => void;
  hiddenOptionIds?: Set<string>;
};

const getCourierLabel = (c: Courier) =>
  c.name || c.title || c.courier_name || '';

const getLevelLabel = (l: Level) => l.display_name || l.name || String(l.id);

export default function CourierSubFilterMenuBody({
  source,
  searchQuery,
  selectedOptions,
  setSelectedOptions,
  hiddenOptionIds = new Set(),
}: CourierSubFilterMenuBodyProps) {
  const [expandedGroups, setExpandedGroups] = useState<Array<string | number>>(
    []
  );
  const [localSelected, setLocalSelected] = useState<Opt[]>(
    selectedOptions || []
  );

  useEffect(() => {
    setLocalSelected(selectedOptions || []);
  }, [selectedOptions]);
  const selectedIds = useMemo(
    () => new Set(localSelected.map((o) => o.id)),
    [localSelected]
  );

  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current || !source.length) return;

    const toExpand = new Set<string | number>();
    source.forEach((c) => {
      const hasSelectedChild = (c.service_levels || []).some((sl) =>
        selectedIds.has(sl.id)
      );
      if (hasSelectedChild) toExpand.add(c.id);
    });

    setExpandedGroups(Array.from(toExpand));
    isInitialized.current = true;
  }, [source, selectedIds]);

  const toggleExpand = (id: Courier['id']) =>
    setExpandedGroups((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  const visibleSource = useMemo(() => {
    return source
      .map((c) => ({
        ...c,
        service_levels: (c.service_levels || []).filter(
          (sl) => !hiddenOptionIds.has(String(sl.id))
        ),
      }))
      .filter((c) => {
        const hasVisibleLevels = (c.service_levels?.length || 0) > 0;
        const parentHidden = hiddenOptionIds.has(String(c.id));
        return hasVisibleLevels || !parentHidden;
      });
  }, [source, hiddenOptionIds]);
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visibleSource;
    return visibleSource
      .map((c) => {
        const parentMatch = Object.entries(c).some(([k, v]) => {
          if (k === 'service_levels') return false;
          return typeof v === 'string' && v.toLowerCase().includes(q);
        });

        const matchingLevels =
          c.service_levels?.filter((sl) =>
            Object.values(sl).some(
              (v) => typeof v === 'string' && v.toLowerCase().includes(q)
            )
          ) ?? [];

        if (parentMatch || matchingLevels.length > 0) {
          return {
            ...c,
            service_levels: parentMatch
              ? (c.service_levels ?? [])
              : matchingLevels,
          };
        }
        return null;
      })
      .filter(Boolean) as Courier[];
  }, [searchQuery, visibleSource]);

  const addMany = (pairs: Opt[]) => {
    const map = new Map<string | number, Opt>();
    localSelected.forEach((o) => map.set(o.id, o));
    pairs.forEach((p) => map.set(p.id, p));
    const updated = Array.from(map.values());
    setLocalSelected(updated);
    setSelectedOptions?.(updated);
  };

  const removeManyByIds = (ids: Array<string | number>) => {
    const idSet = new Set(ids);
    const updated = localSelected.filter((o) => !idSet.has(o.id));
    setLocalSelected(updated);
    setSelectedOptions?.(updated);
  };

  const setParentChecked = (c: Courier, checked: boolean) => {
    const levels = c.service_levels || [];
    if (levels.length) {
      const leafPairs = levels.map((sl) => ({
        id: sl.id,
        name: getLevelLabel(sl),
      }));
      if (checked) {
        addMany(leafPairs);
        setExpandedGroups((prev) => Array.from(new Set([...prev, c.id])));
      } else {
        removeManyByIds(levels.map((sl) => sl.id));
      }
    } else {
      const pair = { id: c.id, name: getCourierLabel(c) };
      if (checked) addMany([pair]);
      else removeManyByIds([c.id]);
    }
  };

  const setChildChecked = (_c: Courier, sl: Level, checked: boolean) => {
    if (checked) addMany([{ id: sl.id, name: getLevelLabel(sl) }]);
    else removeManyByIds([sl.id]);
  };

  const visibleIdList = useMemo(() => {
    const ids: Array<string | number> = [];
    filtered.forEach((c) => {
      const levelIds = (c.service_levels || []).map((sl) => sl.id);
      if (levelIds.length) {
        ids.push(...levelIds);
      } else {
        ids.push(c.id);
      }
    });
    return ids;
  }, [filtered]);

  const visibleSelectedCount = useMemo(
    () =>
      visibleIdList.reduce(
        (acc: number, id) => acc + (selectedIds.has(id) ? 1 : 0),
        0
      ),
    [visibleIdList, selectedIds]
  );

  const allVisibleChecked =
    visibleIdList.length > 0 && visibleSelectedCount === visibleIdList.length;

  const onToggleSelectAllVisible = (checked: boolean) => {
    if (checked) {
      const pairs: Opt[] = [];
      filtered.forEach((c) => {
        const levels = c.service_levels || [];
        if (levels.length) {
          levels.forEach((sl) =>
            pairs.push({ id: sl.id, name: getLevelLabel(sl) })
          );
        } else {
          pairs.push({ id: c.id, name: getCourierLabel(c) });
        }
      });
      addMany(pairs);
      const toExpand = filtered
        .filter((c) => (c.service_levels?.length || 0) > 0)
        .map((c) => c.id);
      setExpandedGroups((prev) => Array.from(new Set([...prev, ...toExpand])));
    } else {
      removeManyByIds(visibleIdList);
    }
  };

  const hasResults = filtered.length > 0;

  return (
    <div style={{ padding: '0 5px 8px' }}>
      {hasResults && (
        <div style={{ marginBottom: 8 }}>
          <Checkbox
            className='custom-menu-shipment-checkbox-only checkbox-sm'
            checked={allVisibleChecked}
            onChange={(e) => onToggleSelectAllVisible(e.target.checked)}
            style={{ fontSize: 12 }}
          >
            Select all
          </Checkbox>
        </div>
      )}

      {filtered.map((c) => {
        const levelIds = (c.service_levels || []).map((sl) => sl.id);
        const groupSelected = levelIds.length
          ? levelIds.every((id) => selectedIds.has(id))
          : selectedIds.has(c.id);
        const isExpanded = expandedGroups.includes(c.id);

        return (
          <div key={c.id} style={{ marginBottom: 8 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Checkbox
                className='custom-menu-shipment-checkbox-only checkbox-sm'
                checked={groupSelected}
                onChange={(e) => setParentChecked(c, e.target.checked)}
                style={{ fontSize: 12 }}
              >
                {getCourierLabel(c)}
                {levelIds.length > 0 ? ` (${levelIds.length})` : ''}
              </Checkbox>

              {!!levelIds.length && (
                <button
                  onClick={() => toggleExpand(c.id)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <ChevronDownIcon
                    className={`chevron-icon ${isExpanded ? 'rotated' : ''}`}
                  />
                </button>
              )}
            </div>

            {isExpanded && !!levelIds.length && (
              <div style={{ marginLeft: 20, marginTop: 6 }}>
                {c.service_levels?.map((sl) => {
                  const isSelected = selectedIds.has(sl.id);
                  return (
                    <Checkbox
                      className='custom-menu-shipment-checkbox-only checkbox-sm'
                      key={sl.id}
                      style={{ display: 'flex', marginBottom: 8, fontSize: 12 }}
                      checked={isSelected}
                      onChange={(e) => setChildChecked(c, sl, e.target.checked)}
                    >
                      {getLevelLabel(sl)}
                    </Checkbox>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
